import { useMemo } from "react";

/**
 * Password strength meter — NIST 800-63B kriterleri temel alınarak:
 * - min 10 karakter
 * - harf + rakam zorunlu (min kabul)
 * - uzunluk 12+ ve özel karakter → güçlü
 * Returns weak/medium/strong.
 */
export default function PasswordStrength({ value }) {
  const { level, label, color, checks } = useMemo(() => {
    const v = value || "";
    const has_len_10 = v.length >= 10;
    const has_letter = /[A-Za-zÇĞİÖŞÜçğıöşü]/.test(v);
    const has_digit = /\d/.test(v);
    const has_special = /[^A-Za-z0-9]/.test(v);
    const has_len_12 = v.length >= 12;
    const has_mixed = /[a-zçğıöşü]/.test(v) && /[A-ZÇĞİÖŞÜ]/.test(v);
    // Simple offline common-password check (partial — server enforces full)
    const lo = v.toLowerCase();
    const is_common = ["password", "12345678", "qwerty123", "admin123", "welcome1", "leranio2026", "sifre1234", "parola123"].some((c) => lo === c || lo.startsWith(c));
    let score = 0;
    if (has_len_10) score += 1;
    if (has_letter && has_digit) score += 1;
    if (has_special) score += 1;
    if (has_len_12) score += 1;
    if (has_mixed) score += 1;
    if (is_common) score = 0;
    let lvl = "weak", lbl = is_common ? "Yaygın · Değiştir" : "Zayıf", clr = "#dc2626";
    if (score >= 4) { lvl = "strong"; lbl = "Güçlü"; clr = "#16a34a"; }
    else if (score >= 2) { lvl = "medium"; lbl = "Orta"; clr = "#f59e0b"; }
    return {
      level: lvl, label: lbl, color: clr,
      checks: { len_10: has_len_10, letter_digit: has_letter && has_digit, special: has_special, len_12: has_len_12, mixed: has_mixed, common: is_common },
    };
  }, [value]);

  if (!value) return null;
  const widths = { weak: "33%", medium: "66%", strong: "100%" };

  return (
    <div className="space-y-1" data-testid="password-strength" data-strength={level}>
      <div className="h-1.5 rounded-full bg-secondary overflow-hidden" role="progressbar" aria-label={`Şifre gücü: ${label}`} aria-valuemin={0} aria-valuemax={3} aria-valuenow={level === "strong" ? 3 : level === "medium" ? 2 : 1}>
        <div className="h-full transition-all" style={{ width: widths[level], background: color }} />
      </div>
      <div className="flex justify-between text-[11px]">
        <span className="text-muted-foreground">Kriterler: 10+ karakter, harf+rakam, özel/karışık/12+</span>
        <span style={{ color }} className="font-medium" data-testid={`strength-label-${level}`}>{label}</span>
      </div>
    </div>
  );
}
